import React, { useState, useEffect } from 'react';
import './App.css';
import axios from 'axios';
import OrderTracker from './OrderTracker';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Checkout state persistence utilities
const CHECKOUT_STATE_KEY = 'homie_checkout_state_v1';

function saveCheckoutState(state) {
  try {
    localStorage.setItem(CHECKOUT_STATE_KEY, JSON.stringify({
      page: state.page,
      formData: state.formData,
      timestamp: Date.now()
    }));
    console.log('💾 Saved checkout state:', state.page);
  } catch (err) {
    console.error('Failed to save checkout state:', err);
  }
}

function readCheckoutState() {
  try {
    const raw = localStorage.getItem(CHECKOUT_STATE_KEY);
    if (!raw) return null;
    const state = JSON.parse(raw);
    // Only restore if less than 1 hour old
    if (Date.now() - state.timestamp > 3600000) {
      clearCheckoutState();
      return null;
    }
    return state;
  } catch (err) {
    console.error('Failed to read checkout state:', err);
    return null;
  }
}

function clearCheckoutState() {
  try {
    localStorage.removeItem(CHECKOUT_STATE_KEY);
  } catch (err) {
    console.error('Failed to clear checkout state:', err);
  }
}

function App() {
  // Initialize page from localStorage to preserve state across re-renders
  const [page, setPage] = useState(() => {
    try {
      const savedState = readCheckoutState();
      const validPages = ['cart', 'checkout', 'success', 'tracker', 'signup-placeholder'];
      if (savedState && savedState.page && validPages.includes(savedState.page)) {
        console.log('📖 Restored page from localStorage:', savedState.page);
        return savedState.page;
      }
    } catch (error) {
      console.error('Error reading checkout state:', error);
      // Clear corrupted state
      try {
        clearCheckoutState();
      } catch (clearError) {
        console.error('Error clearing checkout state:', clearError);
      }
    }
    return 'cart'; // Default to cart if no valid state
  });
  
  // Declare ALL state variables first before using them in useEffect
  const [cartItems, setCartItems] = useState([
    { 
      id: 1, 
      name: 'Nike Air Max 270', 
      price: 129.99, 
      quantity: 1, 
      image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=400&fit=crop&q=80'
    },
    { 
      id: 2, 
      name: 'Running Shorts - Athletic', 
      price: 39.99, 
      quantity: 2, 
      image: 'https://images.unsplash.com/photo-1552902865-b72c031ac5ea?w=400&h=400&fit=crop&q=80'
    }
  ]);
  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    postalCode: '',
    province: '',
    deliveryMethod: 'on-demand',
    paymentMethod: '',
    cardNumber: '',
    cardExpiry: '',
    cardCVC: '',
    cardholderName: ''
  });
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [trackingCode, setTrackingCode] = useState(null);
  const [showHomieModal, setShowHomieModal] = useState(false);
  const [modalMode, setModalMode] = useState('signin'); // 'signin' or 'signup'
  const [signInLoading, setSignInLoading] = useState(false);
  const [signInSuccess, setSignInSuccess] = useState(false);
  // Track if we're in sign-in flow to prevent state saves
  const [isSignInFlow, setIsSignInFlow] = useState(false);
  const [homieCredentials, setHomieCredentials] = useState({
    email: 'user@example.com',
    password: '••••••••'
  });
  
  // Track which fields were auto-filled by Homie for visual feedback
  const [autoFilledFields, setAutoFilledFields] = useState(new Set());
  const [showAutoFillSuccess, setShowAutoFillSuccess] = useState(false);
  
  // Address selection state
  const [showAddressSelection, setShowAddressSelection] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [hoveredAddress, setHoveredAddress] = useState(null);
  
  // Mock saved addresses - some available for Homie, some not
  const savedAddresses = [
    {
      id: 1,
      label: 'Home',
      address: 'Via Roma 123',
      city: 'Milan',
      postalCode: '20100',
      province: 'MI',
      available: true,
      phone: '+39 123 456 7890',
      firstName: 'John',
      lastName: 'Doe'
    },
    {
      id: 2,
      label: 'Work',
      address: 'Corso Buenos Aires 45',
      city: 'Milan',
      postalCode: '20124',
      province: 'MI',
      available: false, // Not available for Homie service
      phone: '+39 123 456 7890',
      firstName: 'John',
      lastName: 'Doe'
    },
    {
      id: 3,
      label: 'Apartment',
      address: 'Via Dante 78',
      city: 'Milan',
      postalCode: '20121',
      province: 'MI',
      available: true,
      phone: '+39 123 456 7890',
      firstName: 'John',
      lastName: 'Doe'
    }
  ];
  
  // Check for fresh visit or reset parameter
  useEffect(() => {
    // Check URL for reset parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('reset') === 'true') {
      clearCheckoutState();
      setPage('cart');
      setFormData({
        phone: '',
        email: '',
        firstName: '',
        lastName: '',
        address: '',
        city: '',
        postalCode: '',
        province: '',
        deliveryMethod: 'on-demand',
        paymentMethod: '',
        cardNumber: '',
        cardExpiry: '',
        cardCVC: '',
        cardholderName: ''
      });
      setAutoFilledFields(new Set());
      // Remove reset parameter from URL
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    
    // Check if this is a fresh visit (no previous state)
    const savedState = readCheckoutState();
    if (!savedState) {
      // Fresh visit - ensure we start at cart
      setPage('cart');
    }
  }, []);
  
  // Update localStorage whenever page changes (but not during sign-in flow)
  // Note: This useEffect must come AFTER all state declarations
  useEffect(() => {
    try {
      // Only save if we're not in the middle of sign-in
      if (!isSignInFlow && !signInLoading && !signInSuccess) {
        saveCheckoutState({ page, formData });
      }
      console.log('📄 Page state changed to:', page);
    } catch (error) {
      console.error('Error in page state effect:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, formData, isSignInFlow, signInLoading, signInSuccess]);
  
  // Debug: Log whenever formData changes
  useEffect(() => {
    console.log('📝 Form data updated:', formData);
  }, [formData]);

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const deliveryFee = formData.deliveryMethod === 'on-demand' ? 4.99 : 2.99;
  const total = subtotal + deliveryFee;

  const handleQuantityChange = (id, delta) => {
    setCartItems(items => 
      items.map(item => 
        item.id === id 
          ? { ...item, quantity: Math.max(1, item.quantity + delta) }
          : item
      )
    );
  };

  const handleRemoveItem = (id) => {
    setCartItems(items => items.filter(item => item.id !== id));
  };

  const handleRestartOrder = () => {
    // Clear all state and start fresh
    clearCheckoutState();
    
    // Reset cart to initial state
    setCartItems([
      { 
        id: 1, 
        name: 'Nike Air Max 270', 
        price: 129.99, 
        quantity: 1, 
        image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=400&fit=crop&q=80'
      },
      { 
        id: 2, 
        name: 'Running Shorts - Athletic', 
        price: 39.99, 
        quantity: 2, 
        image: 'https://images.unsplash.com/photo-1552902865-b72c031ac5ea?w=400&h=400&fit=crop&q=80'
      }
    ]);
    
    // Reset form data
    setFormData({
      phone: '',
      email: '',
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      postalCode: '',
      province: '',
      deliveryMethod: 'on-demand',
      paymentMethod: '',
      cardNumber: '',
      cardExpiry: '',
      cardCVC: '',
      cardholderName: ''
    });
    
    // Reset order tracking
    setOrderId(null);
    setTrackingCode(null);
    
    // Reset modal and auth states
    setShowHomieModal(false);
    setModalMode('signin');
    setSignInLoading(false);
    setSignInSuccess(false);
    setIsSignInFlow(false);
    setAutoFilledFields(new Set());
    setShowAutoFillSuccess(false);
    setShowAddressSelection(false);
    setSelectedAddress(null);
    setHoveredAddress(null);
    
    // Go back to cart
    setPage('cart');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleHomieSSO = () => {
    // CRITICAL: Save current checkout state BEFORE opening modal
    // This ensures we can restore exactly where the user was
    const currentState = {
      page: page === 'checkout' ? 'checkout' : 'checkout', // Force checkout if not already
      formData: formData
    };
    saveCheckoutState(currentState);
    console.log('💾 Saved checkout state before opening modal');
    
    // Ensure we're on checkout page before opening modal
    if (page !== 'checkout') {
      setPage('checkout');
    }
    
    // Open modal to sign in or sign up
    setShowHomieModal(true);
    setModalMode('signin');
    setIsSignInFlow(true);
  };

  const populateDeliveryInfo = () => {
    // Auto-populate delivery info from Homie account
    const savedData = {
      email: homieCredentials.email || 'user@example.com',
      phone: '+39 123 456 7890',
      firstName: 'John',
      lastName: 'Doe',
      address: 'Via Roma 123',
      city: 'Milan',
      postalCode: '20100',
      province: 'MI',
      deliveryMethod: 'on-demand'
    };
    
    // Update form data with saved Homie account info
    setFormData(prev => ({
      ...prev,
      ...savedData
    }));
  };

  const handleSignIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🔵 Sign In clicked - Current page:', page);
    
    // CRITICAL: Save checkout state one more time right before sign-in
    // This captures the exact moment before authentication
    const preSignInState = {
      page: 'checkout', // We MUST be on checkout
      formData: formData
    };
    saveCheckoutState(preSignInState);
    console.log('💾 Saved checkout state before sign-in');
    
    // Show loading state
    setSignInLoading(true);
    
    // Simulate API call with loading
    setTimeout(() => {
      // Auto-populate delivery info from Homie account
      // In production, this would come from your API: /api/me/delivery
      const savedData = {
        email: homieCredentials.email || 'user@example.com',
        phone: '+39 123 456 7890',
        firstName: 'John',
        lastName: 'Doe',
        address: 'Via Roma 123',
        city: 'Milan',
        postalCode: '20100',
        province: 'MI',
        deliveryMethod: 'on-demand'
      };
      
      console.log('🟢 Sign-in successful');
      
      // Hide loading, show address selection
      setSignInLoading(false);
      setSignInSuccess(false);
      setShowAddressSelection(true);
      console.log('📍 Showing address selection');
    }, 1500); // 1.5 second loading
    
    return false;
  };

  const handleAddressSelect = (address) => {
    // Only allow selection of available addresses
    if (!address.available) {
      return;
    }
    
    setSelectedAddress(address);
    console.log('📍 Address selected:', address);
    
    // Show success checkmark
    setSignInSuccess(true);
    setShowAddressSelection(false);
    
    // After showing success, auto-fill form
    setTimeout(() => {
      console.log('🟢 Auto-filling form with selected address');
      
      // Prepare form data from selected address
      const formDataToFill = {
        email: homieCredentials.email || 'user@example.com',
        phone: address.phone,
        firstName: address.firstName,
        lastName: address.lastName,
        address: address.address,
        city: address.city,
        postalCode: address.postalCode,
        province: address.province,
        deliveryMethod: 'on-demand'
      };
      
      // STEP 1: Track which fields will be auto-filled for visual feedback
      const fieldsToFill = ['email', 'phone', 'firstName', 'lastName', 'address', 'city', 'postalCode', 'province', 'deliveryMethod'];
      setAutoFilledFields(new Set(fieldsToFill));
      
      // STEP 2: Update form data with selected address
      setFormData(formDataToFill);
      console.log('🟢 Form data populated');
      
      // STEP 3: Show auto-fill success message
      setShowAutoFillSuccess(true);
      
      // STEP 4: CRITICAL - Force checkout page state IMMEDIATELY
      setPage('checkout');
      localStorage.setItem('checkout_page_state', 'checkout');
      
      // STEP 5: Save the complete state with new form data
      saveCheckoutState({
        page: 'checkout',
        formData: formDataToFill
      });
      console.log('💾 Saved complete checkout state with delivery info');
      
      // STEP 6: Close modal and reset sign-in states
      setShowHomieModal(false);
      setSignInSuccess(false);
      setSignInLoading(false);
      setIsSignInFlow(false);
      setShowAddressSelection(false);
      setSelectedAddress(null);
      console.log('🟢 Modal closed');
      
      // Reset credentials for next time
      setHomieCredentials({ 
        email: 'user@example.com', 
        password: '••••••••' 
      });
      
      // STEP 7: Remove auto-fill highlight after animation completes
      setTimeout(() => {
        setAutoFilledFields(new Set());
        setShowAutoFillSuccess(false);
      }, 3000); // Keep highlight for 3 seconds
      
      // STEP 8: Final guarantee - multiple checks to ensure checkout page
      setTimeout(() => {
        console.log('🟢 Final verification - ensuring checkout page');
        setPage('checkout');
        localStorage.setItem('checkout_page_state', 'checkout');
        
        // Verify we're still on checkout
        const currentPage = localStorage.getItem('checkout_page_state');
        console.log('🟢 Verification complete - page state:', currentPage);
      }, 100);
    }, 800); // Show success checkmark for 0.8 seconds
  };

  const handleSignUp = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent any event bubbling
    
    // Close modal first
    setShowHomieModal(false);
    
    // Reset credentials for next time
    setHomieCredentials({ 
      email: 'user@example.com', 
      password: '••••••••' 
    });
    
    // Navigate to signup placeholder page (future feature)
    setPage('signup-placeholder');
  };

  const closeModal = () => {
    // Preserve page state when closing modal - if we're on checkout, stay on checkout
    const currentPage = page;
    setShowHomieModal(false);
    setSignInLoading(false);
    setSignInSuccess(false);
    setIsSignInFlow(false);
    setShowAddressSelection(false);
    setSelectedAddress(null);
    setHoveredAddress(null);
    setHomieCredentials({ 
      email: 'user@example.com', 
      password: '••••••••' 
    });
    // Ensure page doesn't reset - always preserve checkout if we were there
    if (currentPage === 'checkout') {
      setPage('checkout');
      saveCheckoutState({ page: 'checkout', formData });
    }
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (!formData.phone) {
      alert('Phone number is required');
      return;
    }

    if (formData.deliveryMethod === 'on-demand' && !formData.address) {
      alert('Address is required for on-demand delivery');
      return;
    }

    if (!formData.paymentMethod) {
      alert('Please select a payment method');
      return;
    }

    if (formData.paymentMethod === 'card') {
      if (!formData.cardNumber || !formData.cardExpiry || !formData.cardCVC || !formData.cardholderName) {
        alert('Please fill in all card details');
        return;
      }
    }

    setLoading(true);

    try {
      // Generate tracking code
      const trackingCode = `TRK${Date.now().toString(36).toUpperCase().slice(-8)}`;
      
      const response = await axios.post(`${API_BASE_URL}/checkout/order`, {
        phone: formData.phone,
        email: formData.email,
        address: `${formData.address}, ${formData.city}, ${formData.postalCode}`,
        items: cartItems,
        deliveryMethod: formData.deliveryMethod,
        paymentMethod: formData.paymentMethod,
        storeName: 'Demo Store',
        trackingCode: trackingCode,
        metadata: {
          source: 'web-checkout',
          timestamp: new Date().toISOString()
        }
      });

      setOrderId(response.data.orderId || response.data.order?.id);
      setTrackingCode(response.data.trackingCode || trackingCode);
      setPage('success');
    } catch (error) {
      console.error('Checkout error:', error);
      
      // Use mock data if backend is not available
      if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        const mockOrderId = `ord_${Date.now().toString(36)}`;
        const mockTrackingCode = `TRK${Date.now().toString(36).toUpperCase().slice(-8)}`;
        setOrderId(mockOrderId);
        setTrackingCode(mockTrackingCode);
        setPage('success');
      } else {
        alert(`Error processing order: ${error.response?.data?.error || error.message}\n\nPlease try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign Up Placeholder Page
  if (page === 'signup-placeholder') {
    return (
      <div className="app">
        <div className="placeholder-screen">
          <div className="placeholder-content">
            <div className="placeholder-icon">🚧</div>
            <h1>Future Workflow</h1>
            <p>Sign up functionality will be implemented here in the future.</p>
            <p className="placeholder-subtitle">
              This will allow users to create a new Homie account and save their delivery preferences.
            </p>
            <button className="btn-primary" onClick={() => setPage('checkout')}>
              Back to Checkout
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Order Tracker Page
  if (page === 'tracker') {
    return (
      <OrderTracker 
        orderId={orderId}
        phone={formData.phone}
        onBack={() => setPage('success')}
      />
    );
  }

  // Success Page
  if (page === 'success') {
    return (
      <div className="app success-page-wrapper">
        <div className="success-screen">
          <div className="success-icon-animated">✓</div>
          <h1 className="success-title">Order Confirmed!</h1>
          
          <div className="order-info-success">
            <div className="order-info-item">
              <span className="order-info-label">Order Number</span>
              <span className="order-info-value">#{orderId?.slice(0, 8).toUpperCase() || 'N/A'}</span>
            </div>
          </div>

          <div className="delivery-steps">
            <div className="step-card step-card-1">
              <div className="step-number">1</div>
              <div className="step-icon">📦</div>
              <div className="step-content">
                <h3 className="step-title">Package arrives to your city</h3>
                <p className="step-description">Your order is on its way to our warehouse</p>
              </div>
            </div>
            
            <div className="step-connector"></div>
            
            <div className="step-card step-card-2">
              <div className="step-number">2</div>
              <div className="step-icon">🔔</div>
              <div className="step-content">
                <h3 className="step-title">We'll notify you when ready</h3>
                <p className="step-description">You'll receive a message via WhatsApp</p>
              </div>
            </div>
            
            <div className="step-connector"></div>
            
            <div className="step-card step-card-3">
              <div className="step-number">3</div>
              <div className="step-icon">🚚</div>
              <div className="step-content">
                <h3 className="step-title">We bring it to your door</h3>
                <p className="step-description">Just wait at home, we'll deliver in 45 minutes</p>
              </div>
            </div>
          </div>

          <div className="success-actions">
            <button 
              className="app-button primary"
              onClick={() => window.location.href = 'https://app.noni3.com'}
            >
              Track Your Order
            </button>
            <button 
              className="app-button secondary"
              onClick={handleRestartOrder}
            >
              Start New Order
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Cart Page
  if (page === 'cart') {
    return (
      <div className="app">
        <div className="container">
          <div className="cart-page">
            <h1 className="cart-title">Your Cart ({totalQuantity})</h1>
            
            <div className="cart-layout">
              <div className="cart-items-section">
                {cartItems.map(item => (
                  <div key={item.id} className="cart-item-card">
                    <img src={item.image} alt={item.name} className="item-image" />
                    <div className="item-details">
                      <h3 className="item-name">{item.name}</h3>
                      <div className="quantity-controls">
                        <button 
                          className="quantity-btn"
                          onClick={() => handleQuantityChange(item.id, -1)}
                        >
                          −
                        </button>
                        <span className="quantity">{item.quantity}</span>
                        <button 
                          className="quantity-btn"
                          onClick={() => handleQuantityChange(item.id, 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="item-price-section">
                      <div className="item-price">€{(item.price * item.quantity).toFixed(2)}</div>
                      <button 
                        className="remove-btn"
                        onClick={() => handleRemoveItem(item.id)}
                        title="Remove item"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="order-summary-card">
                <h2 className="summary-title">Summary</h2>
                <div className="summary-row">
                  <span>Items ({totalQuantity} {totalQuantity === 1 ? 'item' : 'items'})</span>
                  <span>€{subtotal.toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping</span>
                  <span>€{deliveryFee.toFixed(2)}</span>
                </div>
                <div className="summary-row total">
                  <span>Total</span>
                  <span>€{total.toFixed(2)}</span>
                </div>

                <button 
                  className="checkout-btn"
                  onClick={() => {
                    // Save state when navigating to checkout
                    setPage('checkout');
                    saveCheckoutState({ page: 'checkout', formData });
                    console.log('💾 Saved state when navigating to checkout');
                  }}
                >
                  Go to Checkout
                </button>
                <p className="returns-text">Free returns up to 30 days</p>
                
                {/* Available Payment Methods */}
                <div className="payment-methods-section">
                  <h3 className="methods-title">Available Payment Methods</h3>
                  <div className="payment-logos">
                    <div className="payment-logo">PayPal</div>
                    <div className="payment-logo">Mastercard</div>
                    <div className="payment-logo">Visa</div>
                    <div className="payment-logo">AMEX</div>
                    <div className="payment-logo">Maestro</div>
                    <div className="payment-logo">Diners</div>
                    <div className="payment-logo">Discover</div>
                    <div className="payment-logo">JCB</div>
                    <div className="payment-logo">UnionPay</div>
                  </div>
                </div>

                {/* Available Shipping Methods */}
                <div className="shipping-methods-section">
                  <h3 className="methods-title">Available Shipping Methods</h3>
                  <div className="shipping-logos">
                    <div className="shipping-logo homie-logo-container">
                      <span className="homie-shipping-logo">
                        H
                        <span className="new-badge-homie">NEW</span>
                      </span>
                      <span className="shipping-name">Homie</span>
                    </div>
                    <div className="shipping-logo">
                      <span className="standard-shipping-logo">📦</span>
                      <span className="shipping-name">Standard</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Checkout Page (default return - the "Quick Checkout" form page)
  // This is where users fill in delivery information
  // After Sign In, users MUST stay on this page with fields auto-filled
  return (
    <div className="app">
      <div className="container">
        <div className="checkout-page">
          <div className="checkout-layout">
            <div className="checkout-form-section">
              <div className="checkout-header">
                <h1 className="checkout-title">Quick Checkout</h1>
                <button 
                  type="button"
                  className="start-over-btn"
                  onClick={() => {
                    clearCheckoutState();
                    setPage('cart');
                    setFormData({
                      phone: '',
                      email: '',
                      firstName: '',
                      lastName: '',
                      address: '',
                      city: '',
                      postalCode: '',
                      province: '',
                      deliveryMethod: 'on-demand',
                      paymentMethod: '',
                      cardNumber: '',
                      cardExpiry: '',
                      cardCVC: '',
                      cardholderName: ''
                    });
                    setAutoFilledFields(new Set());
                    setShowAutoFillSuccess(false);
                  }}
                  title="Start over from the beginning"
                >
                  Start Over
                </button>
              </div>
              
              {/* Auto-fill Success Banner */}
              {showAutoFillSuccess && (
                <div className="autofill-success-banner">
                  <span className="autofill-icon">✓</span>
                  <span className="autofill-message">Your delivery information has been auto-filled by Homie</span>
                </div>
              )}

              <form onSubmit={handleCheckout}>
                <div className="form-section">
                  <h2 className="section-title">Contact</h2>
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className={`form-input ${autoFilledFields.has('email') ? 'autofilled' : ''}`}
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h2 className="section-title">Delivery</h2>
                  
                  {/* Homie SSO Option */}
                  <div className="homie-sso-section">
                    <button type="button" className="homie-sso-btn" onClick={handleHomieSSO}>
                      <span className="homie-logo">H</span>
                      Continue with Homie (Optional)
                    </button>
                    <p className="homie-sso-hint">Save your delivery info and get instant updates</p>
                  </div>

                  {/* Homie Modal */}
                  {showHomieModal && (
                    <div 
                      className="modal-overlay" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Only close if clicking overlay, not content
                        if (e.target === e.currentTarget) {
                          closeModal();
                        }
                      }}
                    >
                      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button 
                          type="button"
                          className="modal-close" 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            closeModal();
                          }}
                        >
                          ×
                        </button>
                        
                        <div className="modal-header">
                          <span className="homie-logo-large">H</span>
                          <h2 className="modal-title">
                            {showAddressSelection 
                              ? 'Select Delivery Address' 
                              : modalMode === 'signin' 
                              ? 'Sign In to Homie' 
                              : 'Create Homie Account'}
                          </h2>
                          <p className="modal-subtitle">
                            {showAddressSelection
                              ? 'Choose an address from your saved addresses'
                              : modalMode === 'signin' 
                              ? 'Sign in to auto-fill your delivery information' 
                              : 'Sign up to save your preferences and track orders'}
                          </p>
                        </div>

                        {!showAddressSelection && (
                          <div className="modal-tabs">
                            <button 
                              className={`modal-tab ${modalMode === 'signin' ? 'active' : ''}`}
                              onClick={() => setModalMode('signin')}
                            >
                              Sign In
                            </button>
                            <button 
                              className={`modal-tab ${modalMode === 'signup' ? 'active' : ''}`}
                              onClick={() => setModalMode('signup')}
                            >
                              Sign Up
                            </button>
                          </div>
                        )}

                        {modalMode === 'signin' ? (
                          <>
                            {showAddressSelection ? (
                              <div className="address-selection-container">
                                <div className="address-list">
                                  {savedAddresses.map((address) => (
                                    <div
                                      key={address.id}
                                      className={`address-card ${address.available ? 'available' : 'unavailable'} ${selectedAddress?.id === address.id ? 'selected' : ''}`}
                                      onClick={() => handleAddressSelect(address)}
                                      onMouseEnter={() => setHoveredAddress(address.id)}
                                      onMouseLeave={() => setHoveredAddress(null)}
                                    >
                                      <div className="address-card-header">
                                        <div className="address-label-section">
                                          <span className="address-label">{address.label}</span>
                                          {address.available && (
                                            <span className="address-available-badge">Available</span>
                                          )}
                                          {!address.available && (
                                            <span className="address-unavailable-badge">Not Available</span>
                                          )}
                                        </div>
                                        {address.available && (
                                          <div className="address-select-icon">✓</div>
                                        )}
                                      </div>
                                      <div className="address-details">
                                        <p className="address-line">{address.address}</p>
                                        <p className="address-city">{address.postalCode} {address.city}, {address.province}</p>
                                      </div>
                                      {!address.available && hoveredAddress === address.id && (
                                        <div className="address-tooltip">
                                          <div className="tooltip-arrow"></div>
                                          <p>Homie service is not available at this address yet. We're expanding our coverage area!</p>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : signInLoading || signInSuccess ? (
                              <div className="modal-loading-state">
                                {signInLoading ? (
                                  <>
                                    <div className="loading-spinner"></div>
                                    <p>Signing in...</p>
                                  </>
                                ) : (
                                  <>
                                    <div className="success-checkmark">✓</div>
                                    <p>Success! Your information has been loaded.</p>
                                  </>
                                )}
                              </div>
                            ) : (
                              <form className="modal-form" onSubmit={handleSignIn} noValidate>
                                <div className="form-group">
                                  <label htmlFor="homie-email">Email</label>
                                  <input
                                    type="email"
                                    id="homie-email"
                                    value={homieCredentials.email}
                                    onChange={(e) => setHomieCredentials({...homieCredentials, email: e.target.value})}
                                    placeholder="your@email.com"
                                    className="form-input"
                                    required
                                  />
                                </div>
                                <div className="form-group">
                                  <label htmlFor="homie-password">Password</label>
                                  <input
                                    type="password"
                                    id="homie-password"
                                    value={homieCredentials.password}
                                    onChange={(e) => setHomieCredentials({...homieCredentials, password: e.target.value})}
                                    placeholder="Enter your password"
                                    className="form-input"
                                    required
                                  />
                                </div>
                                <button 
                                  type="submit" 
                                  className="modal-submit-btn"
                                  onClick={(e) => {
                                    // Extra safeguard
                                    e.preventDefault();
                                    handleSignIn(e);
                                  }}
                                >
                                  Sign In
                                </button>
                                <p className="modal-note">
                                  Your saved delivery information will be automatically filled in
                                </p>
                              </form>
                            )}
                          </>
                        ) : (
                          <form className="modal-form" onSubmit={handleSignUp}>
                            <div className="form-group">
                              <label htmlFor="signup-email">Email</label>
                              <input
                                type="email"
                                id="signup-email"
                                placeholder="your@email.com"
                                className="form-input"
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label htmlFor="signup-password">Password</label>
                              <input
                                type="password"
                                id="signup-password"
                                placeholder="Create a password"
                                className="form-input"
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label htmlFor="signup-phone">Phone Number</label>
                              <input
                                type="tel"
                                id="signup-phone"
                                placeholder="+39 123 456 7890"
                                className="form-input"
                                required
                              />
                            </div>
                            <button type="submit" className="modal-submit-btn">
                              Create Account
                            </button>
                            <p className="modal-note">
                              Coming soon - Full sign up flow will be implemented
                            </p>
                          </form>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label htmlFor="phone">Phone *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+39 123 456 7890"
                      className={`form-input ${autoFilledFields.has('phone') ? 'autofilled' : ''}`}
                      required
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group half">
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`form-input ${autoFilledFields.has('firstName') ? 'autofilled' : ''}`}
                        required
                      />
                    </div>
                    <div className="form-group half">
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`form-input ${autoFilledFields.has('lastName') ? 'autofilled' : ''}`}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="address">Address *</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Street address"
                      className={`form-input ${autoFilledFields.has('address') ? 'autofilled' : ''}`}
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group third">
                      <label htmlFor="postalCode">Postal Code *</label>
                      <input
                        type="text"
                        id="postalCode"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        className={`form-input ${autoFilledFields.has('postalCode') ? 'autofilled' : ''}`}
                        required
                      />
                    </div>
                    <div className="form-group third">
                      <label htmlFor="city">City *</label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className={`form-input ${autoFilledFields.has('city') ? 'autofilled' : ''}`}
                        required
                      />
                    </div>
                    <div className="form-group third">
                      <label htmlFor="province">Province/State</label>
                      <input
                        type="text"
                        id="province"
                        name="province"
                        value={formData.province}
                        onChange={handleInputChange}
                        placeholder="MI"
                        className={`form-input ${autoFilledFields.has('province') ? 'autofilled' : ''}`}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <h3 className="delivery-section-title">Delivery Method *</h3>
                    <p className="delivery-section-subtitle">Choose how to receive your order</p>
                    
                    <div className="delivery-methods">
                      <label className={`delivery-method-option ${formData.deliveryMethod === 'on-demand' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="on-demand"
                          checked={formData.deliveryMethod === 'on-demand'}
                          onChange={handleInputChange}
                          required
                        />
                        <div className="delivery-method-content">
                          <div className="delivery-method-header">
                            <div className="delivery-method-name-with-logo">
                              <span className="homie-method-logo-small">H</span>
                              <span className="delivery-method-name">On-Demand Delivery - Homie</span>
                            </div>
                            <span className="delivery-price">€4.99</span>
                          </div>
                          <div className="delivery-steps-clean">
                            <div className="step-clean">
                              <span className="step-number-clean">1</span>
                              <span className="step-text-clean">Package arrives to your city</span>
                            </div>
                            <div className="step-connector-clean"></div>
                            <div className="step-clean">
                              <span className="step-number-clean">2</span>
                              <span className="step-text-clean">We'll notify you when ready</span>
                            </div>
                            <div className="step-connector-clean"></div>
                            <div className="step-clean">
                              <span className="step-number-clean">3</span>
                              <span className="step-text-clean">We bring it to your door</span>
                            </div>
                          </div>
                          <div className="delivery-method-benefits">
                            <span className="benefit-tag">📱 Request anytime</span>
                            <span className="benefit-tag">⚡ 45-min delivery</span>
                            <span className="benefit-tag">🌱 1.2 kg CO₂ saved</span>
                          </div>
                        </div>
                      </label>

                      <label className={`delivery-method-option ${formData.deliveryMethod === 'standard' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="standard"
                          checked={formData.deliveryMethod === 'standard'}
                          onChange={handleInputChange}
                          required
                        />
                        <div className="delivery-method-content">
                          <div className="delivery-method-header">
                            <div className="delivery-method-name-with-logo">
                              <span className="standard-method-logo-small">📦</span>
                              <span className="delivery-method-name">Standard Delivery</span>
                            </div>
                          </div>
                          <div className="delivery-method-details">
                            <span>Delivery in 3-5 business days</span>
                            <span className="delivery-price">€2.99</span>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Payment Method Section */}
                <div className="form-section">
                  <h2 className="section-title">Payment Method *</h2>
                  <div className="payment-methods-form">
                    <label className={`payment-method-option ${formData.paymentMethod === 'card' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="card"
                        checked={formData.paymentMethod === 'card'}
                        onChange={handleInputChange}
                        required
                      />
                      <div className="payment-method-content">
                        <div className="payment-method-header">
                          <div className="payment-method-name-with-logo">
                            <span className="payment-method-logo">💳</span>
                            <span className="payment-method-name">Credit/Debit Card</span>
                          </div>
                          {formData.paymentMethod === 'card' && (
                            <span className="payment-method-check">✓</span>
                          )}
                        </div>
                      </div>
                    </label>

                    <label className={`payment-method-option ${formData.paymentMethod === 'paypal' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="paypal"
                        checked={formData.paymentMethod === 'paypal'}
                        onChange={handleInputChange}
                        required
                      />
                      <div className="payment-method-content">
                        <div className="payment-method-header">
                          <div className="payment-method-name-with-logo">
                            <span className="payment-method-logo paypal-logo">P</span>
                            <span className="payment-method-name">PayPal</span>
                          </div>
                          {formData.paymentMethod === 'paypal' && (
                            <span className="payment-method-check">✓</span>
                          )}
                        </div>
                      </div>
                    </label>
                  </div>

                  {/* Card Details (shown when card is selected) */}
                  {formData.paymentMethod === 'card' && (
                    <div className="card-details-form">
                      <div className="form-group">
                        <label htmlFor="cardNumber">Card Number *</label>
                        <input
                          type="text"
                          id="cardNumber"
                          name="cardNumber"
                          value={formData.cardNumber}
                          onChange={(e) => {
                            // Format card number with spaces every 4 digits
                            const value = e.target.value.replace(/\s/g, '').replace(/\D/g, '').slice(0, 16);
                            const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                            setFormData({...formData, cardNumber: formatted});
                          }}
                          placeholder="1234 5678 9012 3456"
                          className="form-input"
                          maxLength="19"
                          required
                        />
                      </div>
                      <div className="form-row">
                        <div className="form-group half">
                          <label htmlFor="cardExpiry">Expiry Date *</label>
                          <input
                            type="text"
                            id="cardExpiry"
                            name="cardExpiry"
                            value={formData.cardExpiry}
                            onChange={(e) => {
                              // Format as MM/YY
                              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                              const formatted = value.length > 2 ? `${value.slice(0, 2)}/${value.slice(2)}` : value;
                              setFormData({...formData, cardExpiry: formatted});
                            }}
                            placeholder="MM/YY"
                            className="form-input"
                            maxLength="5"
                            required
                          />
                        </div>
                        <div className="form-group half">
                          <label htmlFor="cardCVC">CVC *</label>
                          <input
                            type="text"
                            id="cardCVC"
                            name="cardCVC"
                            value={formData.cardCVC}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                              setFormData({...formData, cardCVC: value});
                            }}
                            placeholder="123"
                            className="form-input"
                            maxLength="4"
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="cardholderName">Cardholder Name *</label>
                        <input
                          type="text"
                          id="cardholderName"
                          name="cardholderName"
                          value={formData.cardholderName}
                          onChange={handleInputChange}
                          placeholder="John Doe"
                          className="form-input"
                          required
                        />
                      </div>
                    </div>
                  )}
                </div>

                <button 
                  type="submit" 
                  className="submit-order-btn"
                  disabled={loading || !formData.paymentMethod || (formData.paymentMethod === 'card' && (!formData.cardNumber || !formData.cardExpiry || !formData.cardCVC || !formData.cardholderName))}
                >
                  {loading ? 'Processing...' : 'Complete Order'}
                </button>
              </form>
            </div>

            <div className="order-summary-sidebar">
              <h2 className="summary-title">Order Summary</h2>
              {cartItems.map(item => (
                <div key={item.id} className="summary-item">
                  <div className="summary-item-info">
                    <span className="summary-item-name">{item.name}</span>
                    <span className="summary-item-qty">Qty: {item.quantity}</span>
                  </div>
                  <span className="summary-item-price">€{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="summary-totals">
                <div className="summary-row">
                  <span>Subtotal ({totalQuantity} {totalQuantity === 1 ? 'item' : 'items'})</span>
                  <span>€{subtotal.toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping</span>
                  <span>€{deliveryFee.toFixed(2)}</span>
                </div>
                <div className="summary-row total">
                  <span>Total</span>
                  <span>€{total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
